import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { OrderListItem } from '../../services/api';

interface InProgressOrdersProps {
  orders: OrderListItem[];
  loading: boolean;
}

/** Ticket #418 — days an order has been sitting in its current journey stretch. */
function ageDays(createdAt?: string): number | null {
  if (!createdAt) return null;
  const ms = Date.now() - new Date(createdAt).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  return Math.floor(ms / 86_400_000);
}

/**
 * Ticket #418 — view-only tracker for the journey-active (In progress bucket) orders.
 * The one garage-owned action (Mark service complete) deliberately stays on the
 * order detail page; a row here only navigates there.
 */
function InProgressOrders({ orders, loading }: InProgressOrdersProps) {
  const { lang } = useLanguage();
  const ar = lang === 'ar';

  const title = ar ? 'طلبات قيد التنفيذ' : 'Orders in progress';
  const empty = ar ? 'لا توجد طلبات قيد التنفيذ حالياً' : 'No orders in progress right now';
  const readyBadge = ar ? 'جاهز لإكمال الخدمة' : 'Ready to complete';
  const onTheWay = ar ? 'السيارة في الطريق' : 'Car on the way';

  return (
    <div className="partner-orders-feed" data-testid="in-progress-orders">
      <div className="partner-orders-header">
        <h3>{title} ({orders.length})</h3>
      </div>

      {loading && orders.length === 0 ? (
        <div className="partner-orders-empty">…</div>
      ) : orders.length === 0 ? (
        <div className="partner-orders-empty">{empty}</div>
      ) : (
        orders.map((o) => {
          const actionable = o.status === 'IN_PROGRESS' || o.status === 'IN_SERVICE';
          const days = ageDays(o.createdAt);
          return (
            <Link
              key={o.id}
              to={`/orders/${o.id}`}
              className="partner-order-card"
              style={{ display: 'block', textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
            >
              <div className="partner-order-card-top" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className="partner-order-title">
                  {o.orderNumber ? `Order ${o.orderNumber}` : 'Order'}
                </span>
                <span
                  style={{
                    marginInlineStart: 'auto',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    padding: '0.15rem 0.6rem',
                    borderRadius: '999px',
                    backgroundColor: actionable ? '#ede9fe' : '#dbeafe',
                    color: actionable ? '#5b21b6' : '#1e40af',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {actionable ? readyBadge : onTheWay}
                </span>
              </div>

              <div className="partner-order-details">
                <div>{o.serviceName || '—'} · {o.vehicleMakeModel || '—'}</div>
                <div style={{ color: 'var(--text-secondary, #6b7280)', fontSize: '0.85rem' }}>
                  {o.scheduledDate || ''}
                  {o.pickupTimeSlotLabel ? ` · ${o.pickupTimeSlotLabel}` : ''}
                  {days !== null && days > 0 ? ` · ${days}${ar ? ' يوم' : 'd'}` : ''}
                </div>
              </div>
            </Link>
          );
        })
      )}
    </div>
  );
}

export default InProgressOrders;
